import {
  Alert,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Timestamp, collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import { User, Voter, VoterGroup, VoterGroupsFilters } from '../../../types';

import Papa from 'papaparse';
import { db } from '../../../firebase';
import useAuth from '../../../hooks/useAuth';

interface VoterGroupsPrecinctReportProps {
  filters: VoterGroupsFilters
}

interface VoterWithGroup {
  voter: Voter;
  group: VoterGroup;
}

const VoterGroupsPrecinctReport: React.FC<VoterGroupsPrecinctReportProps> = ({ filters }) => {
  const { user } = useAuth();
  
  const [votersWithGroup, setVotersWithGroup] = useState<VoterWithGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchVotersAndPolls = async (user: User) => {
      try {
        setLoading(true);

        const votersRef = collection(db, "voters");
        let q = query(votersRef,
          orderBy("name"),
          where("province", "==", filters.province),
          where("city_municipality", "==", filters.municipality),
          where("barangay", "==", filters.barangay)
        );
        if (filters.precinct) {
          q = query(q, where("precinct", "==", filters.precinct));
        }
        const querySnapshot = await getDocs(q);
        const _votersWithGroup: VoterWithGroup[] = querySnapshot.docs.map(doc => ({
          voter: doc.data() as Voter, 
          group: {
            id: '',
            name: '',
            level: "independent",
            leader: {
              id: '',
              name: ''
            },
            members: [],
            subGroups: [],
            updatedAt: Timestamp.now(),
            updatedBy: '',
            purok: '',
            barangay: '',
            city_municipality: '',
            province: '',
          } as VoterGroup
        }));
        
        const voterGroupsCollection = collection(db, 'voterGroups');
        let voterGroupsQuery = query(voterGroupsCollection, orderBy('name'));
        voterGroupsQuery = query(voterGroupsQuery, where('city_municipality', '==', filters.municipality));
        voterGroupsQuery = query(voterGroupsQuery, where('barangay', '==', filters.barangay));
        const vgQuerySnapshot = await getDocs(voterGroupsQuery);
        for (const group of vgQuerySnapshot.docs) {
          const groupData = group.data() as VoterGroup;
          for (const member of groupData.members) {
            // find the member in _votersWithGroup
            const voter = _votersWithGroup.find(v => v.voter.id === member.id);
            if (voter) {
              voter.group = groupData;
            }
          }
        }

        setVotersWithGroup(_votersWithGroup);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching voters and polls:', err);
        setError('Failed to fetch voters and polls');
        setLoading(false);
      }
    };

    if (user && filters.region && filters.province &&
      filters.district && filters.municipality && filters.barangay
    ) {
      fetchVotersAndPolls(user);
    }
  }, [user, filters]);

  // Updated export to CSV function to include selected candidates
  const exportToCSV = () => {
    // Prepare CSV data
    const csvData = votersWithGroup.map((voterWithGroup, index) => ({
      '#': index + 1,
      'Voter Name': voterWithGroup.voter.name,
      'Leader': voterWithGroup.group.leader.name,
      'Municipality': voterWithGroup.voter.city_municipality,
      'Barangay': voterWithGroup.voter.barangay,
      'Precinct': voterWithGroup.voter.precinct,
    }));

    // Generate CSV file
    const csv = Papa.unparse(csvData);
    
    // Create and download CSV file
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `voters_groups_${filters.barangay}${filters.precinct ? '_precinct_' + filters.precinct : ''}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <Grid container spacing={2} sx={{ mt: 2, justifyContent: 'center' }}>
        <Grid item>
          <CircularProgress />
        </Grid>
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <Alert severity="error">{error}</Alert>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={2} sx={{ mt: 2 }}>
      <Grid item xs={12}>
        <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Grid item>
            <Typography variant="h6" gutterBottom>
              Voters and Leaders in {filters.barangay}
              {filters.precinct ? ` - Precinct ${filters.precinct}` : ' (All Precincts)'}
            </Typography>
          </Grid>
          <Grid item>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={exportToCSV}
              disabled={votersWithGroup.length === 0}
            >
              Export to CSV
            </Button>
          </Grid>
        </Grid>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Voter Name</TableCell>
                <TableCell>Precinct</TableCell>
                <TableCell>Leader</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {votersWithGroup.map((voterWithGroup, index) => (
                <TableRow key={voterWithGroup.voter.id}>
                  <TableCell>{++index}</TableCell>
                  <TableCell>{voterWithGroup.voter.name}</TableCell>
                  <TableCell>{voterWithGroup.voter.precinct}</TableCell>
                  <TableCell>{voterWithGroup.group.leader.name}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );
};

export default VoterGroupsPrecinctReport;
